import React from "react";
import { Box, Button, Stack, Typography, alpha } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BaseDialog from "./BaseDialog";

const normalizeWarning = (warning) =>
  typeof warning === "string" ? { title: warning } : warning || {};

const ValidationWarningDialog = ({
  open,
  title = "Validation warning",
  subtitle = "Some validation checks did not match.",
  description = "You can still save, but these items should be reviewed.",
  warnings = [],
  confirmLabel = "Save anyway",
  cancelLabel = "Review",
  onClose,
  onConfirm,
  loading = false,
}) => {
  const normalizedWarnings = warnings.map(normalizeWarning);

  return (
    <BaseDialog
      open={open}
      onClose={loading ? undefined : onClose}
      title={title}
      subtitle={subtitle}
      icon={<WarningAmberIcon color='warning' />}
      maxWidth='sm'
      closeDisabled={loading}
      paperSx={{ borderRadius: 3 }}
      actions={
        <Stack direction='row' spacing={1.5} justifyContent='flex-end'>
          <Button
            onClick={onClose}
            variant='outlined'
            color='inherit'
            disabled={loading}
            sx={{ borderRadius: 2, px: 2.5 }}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            variant='contained'
            color='warning'
            startIcon={<CheckCircleIcon />}
            disabled={loading}
            sx={{ borderRadius: 2, px: 2.5, boxShadow: "none" }}
          >
            {confirmLabel}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Typography variant='body2' color='text.secondary'>
          {description}
        </Typography>

        <Box
          sx={(theme) => ({
            border: "1px solid",
            borderColor: alpha(theme.palette.warning.main, 0.24),
            bgcolor: alpha(theme.palette.warning.main, 0.06),
            borderRadius: 2,
            maxHeight: 320,
            overflow: "auto",
          })}
        >
          {normalizedWarnings.map((warning, index) => (
            <Box
              key={`${warning.title || "warning"}-${index}`}
              sx={(theme) => ({
                p: 1.75,
                borderBottom:
                  index === normalizedWarnings.length - 1
                    ? "none"
                    : `1px solid ${alpha(theme.palette.warning.main, 0.16)}`,
              })}
            >
              <Typography
                variant='body2'
                sx={{ fontWeight: 700, color: "text.primary" }}
              >
                {warning.title}
              </Typography>
              {warning.description && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: "block", mt: 0.5, lineHeight: 1.5 }}
                >
                  {warning.description}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Stack>
    </BaseDialog>
  );
};

export default ValidationWarningDialog;
